import React, { useState, useEffect, useCallback } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  Chip,
  CircularProgress,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  FormGroup,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import { useSearchParams } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import client from '../../api/client';

const ALL_ROLES = [
  { id: 'admin', label: 'Admin' },
  { id: 'leiter', label: 'Leiter der Höhenrettung' },
  { id: 'lehrhöhenretter_ausbildung', label: 'Lehrhöhenretter, Ber. Aus. & Fortb.' },
  { id: 'lehrhöhenretter_technik', label: 'Lehrhöhenretter, Ber. Tech.' },
  { id: 'lehrhöhenretter_einsatztaktik', label: 'Lehrhöhenretter, Ber. Eins.' },
  { id: 'lehrhöhenretter', label: 'Lehrhöhenretter' },
  { id: 'einheitsführer', label: 'Einheitsführer' },
  { id: 'geräteprüfer', label: 'Geräteprüfer' },
  { id: 'höhenretter', label: 'Höhenretter' },
  { id: 'bewerber_srht', label: 'Bewerber SRHT' },
  { id: 'bewerber_srht_extern', label: 'Bewerber SRHT (Extern)' },
  { id: 'beobachter', label: 'Beobachter' },
  { id: 'extern_partner', label: 'Externer Partner' },
  { id: 'datenaustausch_leser', label: 'Datenaustausch (nur Lesen)' },
];

const WACHBEREITSCHAFT_OPTIONS = [
  { value: '', label: 'Keine Wachbereitschaft' },
  { value: '1_wb', label: '1. Wachbereitschaft' },
  { value: '2_wb', label: '2. Wachbereitschaft' },
];

const WACHBEREITSCHAFT_LABELS = { '1_wb': '1. WB', '2_wb': '2. WB' };

const CAN_ASSIGN_WACHBEREITSCHAFT = ['admin', 'leiter', 'lehrhöhenretter_ausbildung'];
const ROLE_PRIORITY = ALL_ROLES.reduce((acc, role, index) => {
  acc[role.id] = index;
  return acc;
}, {});
const STATUS_META = {
  active: { label: 'Aktiv', color: 'success', order: 1 },
  pending: { label: 'Ausstehend', color: 'warning', order: 2 },
  inactive: { label: 'Inaktiv', color: 'default', order: 3 },
};
const SORT_OPTIONS = [
  { value: 'name', label: 'Alphabet' },
  { value: 'role', label: 'Rolle' },
  { value: 'wachbereitschaft', label: 'Wachbereitschaft' },
  { value: 'status', label: 'Status' },
];
const SORT_DIRECTIONS = [
  { value: 'asc', label: 'Aufsteigend' },
  { value: 'desc', label: 'Absteigend' },
];
const USER_NAME_COLLATOR = new Intl.Collator('de-DE', { sensitivity: 'base', numeric: true });
const BACKUP_ACCESS_ROLES = ['admin', 'leiter', 'lehrhöhenretter_ausbildung'];

function formatDateTime(value) {
  if (!value) {
    return '–';
  }

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return value;
  }

  return new Intl.DateTimeFormat('de-DE', {
    dateStyle: 'medium',
    timeStyle: 'short',
  }).format(date);
}

function formatMonthLabel(monthKey) {
  if (!monthKey || !/^\d{4}-\d{2}$/.test(monthKey)) {
    return monthKey || '–';
  }

  const date = new Date(`${monthKey}-01T00:00:00`);
  return new Intl.DateTimeFormat('de-DE', {
    month: 'long',
    year: 'numeric',
  }).format(date);
}

function parseDownloadFileName(contentDisposition, fallbackFileName) {
  const match = /filename="?([^";]+)"?/i.exec(contentDisposition || '');
  return match?.[1] || fallbackFileName;
}

function triggerBlobDownload(blob, fileName) {
  const objectUrl = window.URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = objectUrl;
  anchor.download = fileName;
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  window.URL.revokeObjectURL(objectUrl);
}

function getBackupRunLabel(run) {
  if (run?.run_category === 'restore') {
    return 'Restore';
  }
  return run?.format === 'csv' ? 'CSV-Export' : 'JSON-Backup';
}

function getScopeGroupLabel(scopeGroup) {
  return scopeGroup?.label || scopeGroup?.id || 'Unbekannter Bereich';
}

function getTableLabel(tableName) {
  const labels = {
    users: 'Benutzer',
    user_roles: 'Benutzerrollen',
    training_hours: 'Logbucheinträge',
    yearly_stats: 'Jahresstatistiken',
    user_year_notes: 'Jahresnotizen',
    entry_change_requests: 'Änderungsanträge',
    audit_log: 'Audit-Log',
    notifications: 'Benachrichtigungen',
    backup_runs: 'Sicherungsläufe',
    assessment_events: 'Prüfungsereignisse',
    assessment_event_candidates: 'Prüfungsteilnehmer',
    assessment_station_results: 'Stationsresultate',
    assessment_station_history: 'Stationshistorie',
    assessment_event_staff: 'Prüfungspersonal',
    pdf_exports: 'PDF-Exportmetadaten',
  };

  return labels[tableName] || tableName;
}

function getDefaultSelectedScopes(scopeGroups) {
  return (scopeGroups || [])
    .filter((scopeGroup) => scopeGroup.defaultSelected || scopeGroup.required)
    .map((scopeGroup) => scopeGroup.id);
}

function getWachbereitschaftLabel(value) {
  return WACHBEREITSCHAFT_LABELS[value] || null;
}

function splitFullName(fullName) {
  const safeFullName = String(fullName || '').trim();
  if (!safeFullName) {
    return { lastName: '', firstName: '' };
  }

  const commaIndex = safeFullName.indexOf(',');
  if (commaIndex === -1) {
    return { lastName: safeFullName, firstName: '' };
  }

  return {
    lastName: safeFullName.slice(0, commaIndex).trim(),
    firstName: safeFullName.slice(commaIndex + 1).trim(),
  };
}

function buildFullName(lastName, firstName) {
  const normalizedLastName = String(lastName || '').trim();
  const normalizedFirstName = String(firstName || '').trim();

  if (normalizedLastName && normalizedFirstName) {
    return `${normalizedLastName}, ${normalizedFirstName}`;
  }

  return normalizedLastName || normalizedFirstName;
}

function getStatusMeta(status) {
  return STATUS_META[status] || { label: status || 'Unbekannt', color: 'default', order: 99 };
}

function getPrimaryRoleId(user) {
  return ALL_ROLES.find((role) => user?.roles?.includes(role.id))?.id || '';
}

function getPrimaryRoleLabel(user) {
  const roleId = getPrimaryRoleId(user);
  return ALL_ROLES.find((role) => role.id === roleId)?.label || 'Keine Rolle';
}

function getWachbereitschaftSortOrder(value) {
  if (value === '1_wb') return 1;
  if (value === '2_wb') return 2;
  return 3;
}

function compareUsers(leftUser, rightUser, sortBy, sortDirection) {
  let compareValue = 0;

  if (sortBy === 'role') {
    compareValue = (ROLE_PRIORITY[getPrimaryRoleId(leftUser)] ?? 99) - (ROLE_PRIORITY[getPrimaryRoleId(rightUser)] ?? 99);
    if (compareValue === 0) {
      compareValue = USER_NAME_COLLATOR.compare(getPrimaryRoleLabel(leftUser), getPrimaryRoleLabel(rightUser));
    }
  } else if (sortBy === 'wachbereitschaft') {
    compareValue = getWachbereitschaftSortOrder(leftUser?.wachbereitschaft) - getWachbereitschaftSortOrder(rightUser?.wachbereitschaft);
    if (compareValue === 0) {
      compareValue = USER_NAME_COLLATOR.compare(
        getWachbereitschaftLabel(leftUser?.wachbereitschaft) || 'Keine Wachbereitschaft',
        getWachbereitschaftLabel(rightUser?.wachbereitschaft) || 'Keine Wachbereitschaft'
      );
    }
  } else if (sortBy === 'status') {
    compareValue = getStatusMeta(leftUser?.status).order - getStatusMeta(rightUser?.status).order;
    if (compareValue === 0) {
      compareValue = USER_NAME_COLLATOR.compare(getStatusMeta(leftUser?.status).label, getStatusMeta(rightUser?.status).label);
    }
  } else {
    compareValue = USER_NAME_COLLATOR.compare(leftUser?.full_name || '', rightUser?.full_name || '');
  }

  if (compareValue === 0) {
    compareValue = USER_NAME_COLLATOR.compare(leftUser?.full_name || '', rightUser?.full_name || '');
  }
  if (compareValue === 0) {
    compareValue = Number(leftUser?.id || 0) - Number(rightUser?.id || 0);
  }

  return sortDirection === 'desc' ? compareValue * -1 : compareValue;
}

// ---------------------------------------------------------------------------
// Expandable user row
// ---------------------------------------------------------------------------

function UserRow({
  user,
  currentUser,
  expanded,
  onToggle,
  actionLoading,
  canAssignWachbereitschaft,
  canResetPasswords,
  wachbereitschaftDraft,
  onWachbereitschaftDraftChange,
  onSaveWachbereitschaft,
  onRoleToggle,
  onActivate,
  onDeactivate,
  onDelete,
  onOpenPasswordReset,
  profileDraft,
  onProfileDraftChange,
  onRequestProfileSave,
}) {
  const isLoading = actionLoading === user.id;
  const wbLabel = getWachbereitschaftLabel(user.wachbereitschaft);
  const statusMeta = getStatusMeta(user.status);
  const assembledDraftFullName = buildFullName(profileDraft?.lastName, profileDraft?.firstName);
  const hasProfileChanges = Boolean(
    profileDraft && (
      assembledDraftFullName !== (user.full_name || '') ||
      (profileDraft.email || '') !== (user.email || '')
    )
  );

  return (
    <>
      <TableRow hover>
        <TableCell>{user.full_name}</TableCell>
        <TableCell sx={{ color: 'text.secondary', fontSize: '0.85rem' }}>{user.email}</TableCell>
        <TableCell>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
            {user.roles && user.roles.length > 0 ? (
              user.roles.map((roleId) => {
                const roleObj = ALL_ROLES.find((r) => r.id === roleId);
                return (
                  <Chip
                    key={roleId}
                    label={roleObj ? roleObj.label : roleId}
                    size="small"
                    variant="outlined"
                  />
                );
              })
            ) : (
              <Typography variant="body2" color="text.secondary">
                Keine Rolle
              </Typography>
            )}
          </Box>
        </TableCell>
        <TableCell>
          {wbLabel ? (
            <Chip label={wbLabel} size="small" color="primary" variant="outlined" />
          ) : (
            <Typography variant="body2" color="text.secondary">
              –
            </Typography>
          )}
        </TableCell>
        <TableCell>
          <Chip
            label={statusMeta.label}
            size="small"
            color={statusMeta.color}
          />
        </TableCell>
        <TableCell>
          <IconButton size="small" onClick={onToggle} aria-label="Zeile aufklappen">
            {expanded ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
          </IconButton>
        </TableCell>
      </TableRow>

      <TableRow>
        <TableCell colSpan={6} sx={{ py: 0, border: expanded ? undefined : 'none' }}>
          <Collapse in={expanded} timeout="auto" unmountOnExit>
            <Box sx={{ py: 2, px: { xs: 1, sm: 2 } }}>
              <Stack spacing={3}>
                <Box>
                  <Typography variant="subtitle2" gutterBottom>
                    Stammdaten
                  </Typography>
                  <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} alignItems={{ xs: 'stretch', sm: 'flex-start' }}>
                    <TextField
                      label="Nachname"
                      size="small"
                      fullWidth
                      value={profileDraft?.lastName || ''}
                      onChange={(event) => onProfileDraftChange(user.id, 'lastName', event.target.value)}
                      disabled={isLoading}
                    />
                    <TextField
                      label="Vorname"
                      size="small"
                      fullWidth
                      value={profileDraft?.firstName || ''}
                      onChange={(event) => onProfileDraftChange(user.id, 'firstName', event.target.value)}
                      disabled={isLoading}
                    />
                    <TextField
                      label="E-Mail"
                      type="email"
                      size="small"
                      fullWidth
                      value={profileDraft?.email || ''}
                      onChange={(event) => onProfileDraftChange(user.id, 'email', event.target.value)}
                      disabled={isLoading}
                    />
                    <Button
                      variant="contained"
                      size="small"
                      onClick={() => onRequestProfileSave(user.id)}
                      disabled={isLoading || !hasProfileChanges}
                      sx={{ minWidth: 132 }}
                    >
                      Speichern
                    </Button>
                  </Stack>
                  <Typography variant="caption" color="text.secondary" sx={{ mt: 0.75, display: 'block' }}>
                    Änderungen an Name und E-Mail werden erst nach expliziter Bestätigung übernommen.
                  </Typography>
                </Box>

                {/* Role checkboxes */}
                <Box>
                  <Typography variant="subtitle2" gutterBottom>
                    Rollen für {user.full_name}
                  </Typography>
                  <FormGroup row sx={{ gap: 0 }}>
                    {ALL_ROLES.map((role) => {
                      if (
                        role.id === 'admin' &&
                        !(currentUser?.roles?.includes('admin'))
                      ) {
                        return null;
                      }
                      return (
                        <FormControlLabel
                          key={role.id}
                          control={
                            <Checkbox
                              size="small"
                              checked={!!(user.roles && user.roles.includes(role.id))}
                              onChange={() => onRoleToggle(user.id, role.id)}
                              disabled={isLoading}
                            />
                          }
                          label={<Typography variant="body2">{role.label}</Typography>}
                          sx={{ mr: 2, mb: 0.5 }}
                        />
                      );
                    })}
                  </FormGroup>
                  {isLoading && (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1 }}>
                      <CircularProgress size={16} />
                      <Typography variant="caption" color="text.secondary">
                        Wird gespeichert…
                      </Typography>
                    </Box>
                  )}
                </Box>

                {/* Wachbereitschaft */}
                <Box>
                  <Typography variant="subtitle2" gutterBottom>
                    Wachbereitschaft
                  </Typography>
                  <Stack direction="row" spacing={1.5} alignItems="center">
                    <FormControl size="small" sx={{ minWidth: 220 }}>
                      <InputLabel id={`wb-label-${user.id}`}>Wachbereitschaft</InputLabel>
                      <Select
                        labelId={`wb-label-${user.id}`}
                        value={wachbereitschaftDraft ?? ''}
                        label="Wachbereitschaft"
                        onChange={(e) => onWachbereitschaftDraftChange(user.id, e.target.value)}
                        disabled={!canAssignWachbereitschaft || isLoading}
                      >
                        {WACHBEREITSCHAFT_OPTIONS.map((opt) => (
                          <MenuItem key={opt.value || 'none'} value={opt.value}>
                            {opt.label}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                    <Button
                      variant="contained"
                      size="small"
                      onClick={() => onSaveWachbereitschaft(user.id)}
                      disabled={!canAssignWachbereitschaft || isLoading}
                    >
                      Speichern
                    </Button>
                  </Stack>
                  {!canAssignWachbereitschaft && (
                    <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                      Nur Admin, Leiter und Lehrhöhenretter Ausbildung dürfen diese Zuordnung ändern.
                    </Typography>
                  )}
                </Box>

                {/* Action buttons */}
                <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                  {canResetPasswords && (
                    <Button
                      variant="outlined"
                      size="small"
                      onClick={() => onOpenPasswordReset(user)}
                      disabled={isLoading}
                    >
                      Passwort zurücksetzen
                    </Button>
                  )}
                  {user.status === 'active' && (
                    <Button
                      variant="outlined"
                      color="error"
                      size="small"
                      onClick={() => onDeactivate(user.id)}
                      disabled={isLoading}
                    >
                      Deaktivieren
                    </Button>
                  )}
                  {user.status === 'inactive' && (
                    <>
                      <Button
                        variant="outlined"
                        color="success"
                        size="small"
                        onClick={() => onActivate(user.id)}
                        disabled={isLoading}
                      >
                        Aktivieren
                      </Button>
                      {currentUser?.roles?.includes('admin') && (
                        <Button
                          variant="outlined"
                          color="error"
                          size="small"
                          onClick={() => onDelete(user.id)}
                          disabled={isLoading}
                        >
                          Löschen
                        </Button>
                      )}
                    </>
                  )}
                </Stack>
              </Stack>
            </Box>
          </Collapse>
        </TableCell>
      </TableRow>
    </>
  );
}

// ---------------------------------------------------------------------------
// New-user form card
// ---------------------------------------------------------------------------

function NewUserForm({ onCancel, onCreated, setError }) {
  const [form, setForm] = useState({
    email: '',
    password: '',
    firstName: '',
    lastName: '',
    roles: ['höhenretter'],
    wachbereitschaft: '',
  });
  const [creating, setCreating] = useState(false);

  const patch = (field) => (e) => setForm((prev) => ({ ...prev, [field]: e.target.value }));

  const toggleRole = (roleId) => {
    setForm((prev) => ({
      ...prev,
      roles: prev.roles.includes(roleId)
        ? prev.roles.filter((r) => r !== roleId)
        : [...prev.roles, roleId],
    }));
  };

  const handleCreate = async () => {
    if (!form.email || !form.password || !form.firstName || !form.lastName) {
      setError('Alle Felder erforderlich');
      return;
    }
    setCreating(true);
    try {
      await client.post('/api/admin/create-user', {
        email: form.email,
        password: form.password,
        firstName: form.firstName,
        lastName: form.lastName,
        roles: form.roles,
        wachbereitschaft: form.wachbereitschaft || null,
      });
      setError('');
      onCreated();
    } catch (err) {
      setError(err.response?.data?.error || 'Fehler beim Erstellen');
    } finally {
      setCreating(false);
    }
  };

  return (
    <Card sx={{ mb: 3 }}>
      <CardContent>
        <Typography variant="h6" gutterBottom>
          Neuen Benutzer erstellen
        </Typography>
        <Stack spacing={2}>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
            <TextField
              label="Nachname"
              size="small"
              fullWidth
              value={form.lastName}
              onChange={patch('lastName')}
            />
            <TextField
              label="Vorname"
              size="small"
              fullWidth
              value={form.firstName}
              onChange={patch('firstName')}
            />
          </Stack>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
            <TextField
              label="E-Mail"
              type="email"
              size="small"
              fullWidth
              value={form.email}
              onChange={patch('email')}
            />
            <TextField
              label="Passwort"
              type="password"
              size="small"
              fullWidth
              value={form.password}
              onChange={patch('password')}
            />
          </Stack>

          <FormControl size="small" sx={{ maxWidth: 280 }}>
            <InputLabel id="new-user-wb-label">Wachbereitschaft</InputLabel>
            <Select
              labelId="new-user-wb-label"
              value={form.wachbereitschaft}
              label="Wachbereitschaft"
              onChange={(e) => setForm((prev) => ({ ...prev, wachbereitschaft: e.target.value }))}
            >
              {WACHBEREITSCHAFT_OPTIONS.map((opt) => (
                <MenuItem key={opt.value || 'none'} value={opt.value}>
                  {opt.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <Box>
            <Typography variant="subtitle2" gutterBottom>
              Rollen
            </Typography>
            <FormGroup row sx={{ gap: 0 }}>
              {ALL_ROLES.map((role) => (
                <FormControlLabel
                  key={role.id}
                  control={
                    <Checkbox
                      size="small"
                      checked={form.roles.includes(role.id)}
                      onChange={() => toggleRole(role.id)}
                    />
                  }
                  label={<Typography variant="body2">{role.label}</Typography>}
                  sx={{ mr: 2, mb: 0.5 }}
                />
              ))}
            </FormGroup>
          </Box>

          <Stack direction="row" spacing={1.5}>
            <Button
              variant="contained"
              onClick={handleCreate}
              disabled={creating}
              startIcon={creating ? <CircularProgress size={16} color="inherit" /> : null}
            >
              {creating ? 'Wird erstellt…' : 'Erstellen'}
            </Button>
            <Button variant="outlined" onClick={onCancel} disabled={creating}>
              Abbrechen
            </Button>
          </Stack>
        </Stack>
      </CardContent>
    </Card>
  );
}

// ---------------------------------------------------------------------------
// Password-reset dialog
// ---------------------------------------------------------------------------

function PasswordResetDialog({ dialog, onClose, onSubmit, actionLoading }) {
  const isLoading = actionLoading === dialog.userId;

  return (
    <Dialog open={dialog.open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>Passwort zurücksetzen für {dialog.userName}</DialogTitle>
      <DialogContent>
        {dialog.resetError && (
          <Alert severity="error" sx={{ mt: 1, mb: 2 }}>
            {dialog.resetError}
          </Alert>
        )}
        {dialog.resetSuccess && (
          <Alert severity="success" sx={{ mt: 1, mb: 2 }}>
            {dialog.resetSuccess}
          </Alert>
        )}
        <form id="password-reset-form" onSubmit={onSubmit}>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              autoFocus
              label="Neues Passwort"
              type="password"
              fullWidth
              value={dialog.password}
              onChange={(e) =>
                onClose.__updateDialog({ ...dialog, password: e.target.value, resetError: '', resetSuccess: '' })
              }
            />
            <TextField
              label="Neues Passwort wiederholen"
              type="password"
              fullWidth
              value={dialog.confirmPassword}
              onChange={(e) =>
                onClose.__updateDialog({ ...dialog, confirmPassword: e.target.value, resetError: '', resetSuccess: '' })
              }
            />
            <Typography variant="caption" color="text.secondary">
              Das Passwort muss mindestens 8 Zeichen lang sein.
            </Typography>
          </Stack>
        </form>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button variant="outlined" onClick={onClose}>
          Abbrechen
        </Button>
        <Button
          type="submit"
          form="password-reset-form"
          variant="contained"
          disabled={isLoading || Boolean(dialog.resetSuccess)}
          startIcon={isLoading ? <CircularProgress size={16} color="inherit" /> : null}
        >
          {isLoading ? 'Wird gespeichert…' : 'Neues Passwort speichern'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

function UserIdentityConfirmDialog({ dialog, onClose, onConfirm, actionLoading }) {
  const isLoading = actionLoading === dialog.userId;

  return (
    <Dialog open={dialog.open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>Änderungen bestätigen</DialogTitle>
      <DialogContent>
        {dialog.error && (
          <Alert severity="error" sx={{ mt: 1, mb: 2 }}>
            {dialog.error}
          </Alert>
        )}
        <Stack spacing={2} sx={{ mt: 1 }}>
          <Typography variant="body2" color="text.secondary">
            Bitte bestätigen Sie die folgenden Änderungen der Benutzerdaten.
          </Typography>
          <Box>
            <Typography variant="subtitle2">Vollständiger Name</Typography>
            <Typography variant="body2" color="text.secondary">
              Bisher: {dialog.currentFullName || '–'}
            </Typography>
            <Typography variant="body2">
              Neu: {dialog.nextFullName || '–'}
            </Typography>
          </Box>
          <Box>
            <Typography variant="subtitle2">E-Mail</Typography>
            <Typography variant="body2" color="text.secondary">
              Bisher: {dialog.currentEmail || '–'}
            </Typography>
            <Typography variant="body2">
              Neu: {dialog.nextEmail || '–'}
            </Typography>
          </Box>
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button variant="outlined" onClick={onClose} disabled={isLoading}>
          Abbrechen
        </Button>
        <Button
          variant="contained"
          onClick={onConfirm}
          disabled={isLoading}
          startIcon={isLoading ? <CircularProgress size={16} color="inherit" /> : null}
        >
          {isLoading ? 'Wird gespeichert…' : 'Änderungen bestätigen'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

function BackupDialog({
  open,
  onClose,
  overview,
  loading,
  actionLoading,
  error,
  success,
  canRestoreBackups,
  onExportJson,
  onExportCsv,
  selectedScopeIds,
  onScopeToggle,
  restoreValidation,
  onValidateRestore,
  onConfirmRestore,
}) {
  const monthlyBackup = overview?.monthlyBackup || null;
  const availableScopeGroups = overview?.scopeGroups || [];

  return (
    <Dialog open={open} onClose={actionLoading ? undefined : onClose} fullWidth maxWidth="md">
      <DialogTitle>Datensicherung</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <Alert severity="info">
            Das JSON-Backup ist das Rücksicherungsformat. Der CSV-Export ist für Excel und externe Sichtprüfung gedacht.
          </Alert>

          {error && <Alert severity="error">{error}</Alert>}
          {success && <Alert severity="success">{success}</Alert>}

          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          ) : (
            <>
              {monthlyBackup ? (
                <Alert severity="success">
                  Die monatliche JSON-Datensicherung für {formatMonthLabel(overview?.currentMonth)} wurde am {formatDateTime(monthlyBackup.created_at)} von {monthlyBackup.created_by_name || 'unbekannt'} durchgeführt.
                </Alert>
              ) : (
                <Alert severity="warning">
                  Für {formatMonthLabel(overview?.currentMonth)} liegt noch keine rücksicherbare JSON-Datensicherung vor.
                </Alert>
              )}

              <Card variant="outlined">
                <CardContent>
                  <Typography variant="subtitle1" gutterBottom>
                    Aktionen
                  </Typography>
                  {availableScopeGroups.length > 0 && (
                    <Stack spacing={0.75} sx={{ mb: 1.5 }}>
                      <Typography variant="body2" color="text.secondary">
                        Aktueller Sicherungsumfang
                      </Typography>
                      <FormGroup>
                        {availableScopeGroups.map((scopeGroup) => (
                          <FormControlLabel
                            key={scopeGroup.id}
                            control={
                              <Checkbox
                                size="small"
                                checked={selectedScopeIds.includes(scopeGroup.id)}
                                disabled={scopeGroup.required || Boolean(actionLoading)}
                                onChange={() => onScopeToggle(scopeGroup.id)}
                              />
                            }
                            label={
                              <Box>
                                <Typography variant="body2">
                                  {getScopeGroupLabel(scopeGroup)}
                                </Typography>
                                <Typography variant="caption" color="text.secondary">
                                  {scopeGroup.tables.map((tableName) => getTableLabel(tableName)).join(', ')}
                                </Typography>
                              </Box>
                            }
                            sx={{ alignItems: 'flex-start', mb: 0.5 }}
                          />
                        ))}
                      </FormGroup>
                    </Stack>
                  )}
                  <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
                    <Button
                      variant="contained"
                      onClick={onExportJson}
                      disabled={Boolean(actionLoading)}
                    >
                      {actionLoading === 'json' ? 'Erzeuge JSON-Backup…' : 'JSON-Backup herunterladen'}
                    </Button>
                    <Button
                      variant="outlined"
                      onClick={onExportCsv}
                      disabled={Boolean(actionLoading)}
                    >
                      {actionLoading === 'csv' ? 'Erzeuge CSV-Export…' : 'CSV für Excel herunterladen'}
                    </Button>
                    {canRestoreBackups && (
                      <Button
                        variant="outlined"
                        color="warning"
                        component="label"
                        disabled={Boolean(actionLoading)}
                      >
                        {actionLoading === 'validate-restore' ? 'Prüfe Sicherung…' : 'JSON-Backup prüfen'}
                        <input
                          type="file"
                          accept="application/json,.json"
                          hidden
                          onChange={onValidateRestore}
                        />
                      </Button>
                    )}
                  </Stack>
                  {canRestoreBackups && (
                    <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                      Vor dem Einspielen wird die Datei geprüft. Erst danach kann der Restore explizit bestätigt werden.
                    </Typography>
                  )}
                </CardContent>
              </Card>

              {canRestoreBackups && restoreValidation && (
                <Card variant="outlined">
                  <CardContent>
                    <Typography variant="subtitle1" gutterBottom>
                      Restore-Prüfung
                    </Typography>
                    <Stack spacing={1.25}>
                      <Typography variant="body2">
                        Datei: {restoreValidation.fileName || 'unbekannt'}
                      </Typography>
                      <Typography variant="body2">
                        Formatversion: {restoreValidation.version || '–'}
                      </Typography>
                      <Typography variant="body2">
                        Exportzeitpunkt: {formatDateTime(restoreValidation.exportedAt)}
                      </Typography>
                      <Typography variant="body2">
                        Exportmonat: {formatMonthLabel(restoreValidation.exportedMonth)}
                      </Typography>
                      <Typography variant="body2">
                        Datensätze: {restoreValidation.recordCount || 0}
                      </Typography>

                      {(restoreValidation.scopeGroups || []).length > 0 && (
                        <Box>
                          <Typography variant="body2" color="text.secondary" sx={{ mb: 0.75 }}>
                            Enthaltene Bereiche
                          </Typography>
                          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                            {restoreValidation.scopeGroups
                              .filter((scopeGroup) => scopeGroup.included)
                              .map((scopeGroup) => (
                                <Chip
                                  key={scopeGroup.id}
                                  size="small"
                                  color="primary"
                                  variant="outlined"
                                  label={`${getScopeGroupLabel(scopeGroup)} (${scopeGroup.recordCount || 0})`}
                                />
                              ))}
                          </Stack>
                        </Box>
                      )}

                      {restoreValidation.stats && Object.keys(restoreValidation.stats).length > 0 && (
                        <Box>
                          <Typography variant="body2" color="text.secondary" sx={{ mb: 0.75 }}>
                            Enthaltene Tabellen
                          </Typography>
                          <Stack spacing={0.5}>
                            {Object.entries(restoreValidation.stats)
                              .filter(([, count]) => Number(count) > 0)
                              .map(([tableName, count]) => (
                                <Typography key={tableName} variant="body2">
                                  {getTableLabel(tableName)}: {count}
                                </Typography>
                              ))}
                          </Stack>
                        </Box>
                      )}

                      {(restoreValidation.warnings || []).map((warning) => (
                        <Alert key={warning} severity="warning">
                          {warning}
                        </Alert>
                      ))}

                      {(restoreValidation.errors || []).map((validationError) => (
                        <Alert key={validationError} severity="error">
                          {validationError}
                        </Alert>
                      ))}

                      <Button
                        variant="contained"
                        color="warning"
                        onClick={onConfirmRestore}
                        disabled={Boolean(actionLoading) || !restoreValidation.valid}
                      >
                        {actionLoading === 'restore' ? 'Spiele Sicherung ein…' : 'Restore jetzt ausführen'}
                      </Button>
                    </Stack>
                  </CardContent>
                </Card>
              )}

              <Card variant="outlined">
                <CardContent>
                  <Typography variant="subtitle1" gutterBottom>
                    Letzte Läufe
                  </Typography>
                  <Stack spacing={1.25}>
                    {(overview?.recentRuns || []).length === 0 ? (
                      <Typography variant="body2" color="text.secondary">
                        Noch keine Datensicherungen oder Restores vorhanden.
                      </Typography>
                    ) : (
                      overview.recentRuns.map((run) => (
                        <Box
                          key={run.id}
                          sx={{
                            border: '1px solid',
                            borderColor: 'divider',
                            borderRadius: 1,
                            px: 1.5,
                            py: 1.25,
                          }}
                        >
                          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} justifyContent="space-between">
                            <Box>
                              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                {getBackupRunLabel(run)}
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                {formatDateTime(run.created_at)}
                              </Typography>
                            </Box>
                            <Box>
                              <Chip label={run.format?.toUpperCase() || '–'} size="small" variant="outlined" sx={{ mr: 1 }} />
                              <Chip label={`${run.record_count || 0} Datensätze`} size="small" />
                            </Box>
                          </Stack>
                          <Typography variant="body2" sx={{ mt: 0.75 }}>
                            Ausgeführt von: {run.created_by_name || 'unbekannt'}
                          </Typography>
                          {Array.isArray(run.meta?.scopeGroups) && run.meta.scopeGroups.some((scopeGroup) => scopeGroup.included) && (
                            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mt: 0.75 }}>
                              {run.meta.scopeGroups
                                .filter((scopeGroup) => scopeGroup.included)
                                .map((scopeGroup) => (
                                  <Chip
                                    key={`${run.id}-${scopeGroup.id}`}
                                    label={getScopeGroupLabel(scopeGroup)}
                                    size="small"
                                    variant="outlined"
                                  />
                                ))}
                            </Stack>
                          )}
                          {run.file_name && (
                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                              Datei: {run.file_name}
                            </Typography>
                          )}
                        </Box>
                      ))
                    )}
                  </Stack>
                </CardContent>
              </Card>
            </>
          )}
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button variant="outlined" onClick={onClose} disabled={Boolean(actionLoading)}>
          Schließen
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// ---------------------------------------------------------------------------
// Main page
// ---------------------------------------------------------------------------

function BenutzerPage() {
  const { user: currentUser, reloadUser } = useAuth();
  const [searchParams] = useSearchParams();

  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [actionLoading, setActionLoading] = useState(null);
  const [expandedUserId, setExpandedUserId] = useState(null);
  const [showNewUserForm, setShowNewUserForm] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState('');
  const [backupDialogOpen, setBackupDialogOpen] = useState(() => searchParams.get('backup') === 'open');
  const [backupOverview, setBackupOverview] = useState(null);
  const [backupLoading, setBackupLoading] = useState(false);
  const [backupActionLoading, setBackupActionLoading] = useState('');
  const [backupError, setBackupError] = useState('');
  const [backupSuccess, setBackupSuccess] = useState('');
  const [selectedBackupScopeIds, setSelectedBackupScopeIds] = useState([]);
  const [restoreValidation, setRestoreValidation] = useState(null);
  const [restoreFile, setRestoreFile] = useState(null);
  const [sortBy, setSortBy] = useState('name');
  const [sortDirection, setSortDirection] = useState('asc');
  const [wachbereitschaftDrafts, setWachbereitschaftDrafts] = useState({});
  const [profileDrafts, setProfileDrafts] = useState({});
  const [passwordDialog, setPasswordDialog] = useState({
    open: false,
    userId: null,
    userName: '',
    password: '',
    confirmPassword: '',
    resetError: '',
    resetSuccess: '',
  });
  const [identityConfirmDialog, setIdentityConfirmDialog] = useState({
    open: false,
    userId: null,
    currentFullName: '',
    currentEmail: '',
    nextFullName: '',
    nextEmail: '',
    error: '',
  });

  const canAssignWachbereitschaft =
    currentUser?.roles?.some((r) => CAN_ASSIGN_WACHBEREITSCHAFT.includes(r)) ?? false;

  const canResetPasswords = currentUser?.roles?.includes('admin') ?? false;
  const canManageBackups = currentUser?.roles?.some((role) => BACKUP_ACCESS_ROLES.includes(role)) ?? false;
  const canRestoreBackups = currentUser?.roles?.includes('admin') ?? false;

  // -------------------------------------------------------------------------
  // Data loading
  // -------------------------------------------------------------------------

  const loadUsers = useCallback(async () => {
    try {
      setLoading(true);
      const response = await client.get('/api/admin/users');
      const fetchedUsers = response.data.users || [];
      setUsers(fetchedUsers);
      const drafts = fetchedUsers.reduce((acc, u) => {
        acc[u.id] = u.wachbereitschaft || '';
        return acc;
      }, {});
      const nextProfileDrafts = fetchedUsers.reduce((acc, u) => {
        const splitName = splitFullName(u.full_name);
        acc[u.id] = {
          lastName: splitName.lastName,
          firstName: splitName.firstName,
          email: u.email || '',
        };
        return acc;
      }, {});
      setWachbereitschaftDrafts(drafts);
      setProfileDrafts(nextProfileDrafts);
      setError('');
    } catch (err) {
      setError('Fehler beim Laden der Benutzer');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadUsers();
  }, [loadUsers]);

  const loadBackupOverview = useCallback(async () => {
    try {
      setBackupLoading(true);
      const response = await client.get('/api/admin/backups/overview');
      setBackupOverview(response.data || null);
      setSelectedBackupScopeIds((previousValue) => {
        if (previousValue.length > 0) {
          return previousValue;
        }
        return getDefaultSelectedScopes(response.data?.scopeGroups || []);
      });
      setBackupError('');
    } catch (err) {
      setBackupError(err.response?.data?.error || 'Fehler beim Laden des Backup-Status');
    } finally {
      setBackupLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!backupDialogOpen || !canManageBackups) {
      return;
    }

    loadBackupOverview();
  }, [backupDialogOpen, canManageBackups, loadBackupOverview]);

  // -------------------------------------------------------------------------
  // Role toggling
  // -------------------------------------------------------------------------

  const handleRoleToggle = async (userId, roleName) => {
    const targetUser = users.find((u) => u.id === userId);
    if (!targetUser || !Array.isArray(targetUser.roles)) return;

    const newRoles = targetUser.roles.includes(roleName)
      ? targetUser.roles.filter((r) => r !== roleName)
      : [...targetUser.roles, roleName];

    if (newRoles.length === 0) {
      setError('Mindestens eine Rolle erforderlich');
      return;
    }

    if (newRoles.includes('admin') && !currentUser?.roles?.includes('admin')) {
      setError('Nur Admin kann Admin-Rolle vergeben');
      return;
    }

    setActionLoading(userId);
    try {
      await client.post(`/api/admin/change-roles/${userId}`, { roles: newRoles });
      await loadUsers();
      await reloadUser();
      setError('');
    } catch (err) {
      setError(err.response?.data?.error || 'Fehler beim Ändern der Rollen');
    } finally {
      setActionLoading(null);
    }
  };

  // -------------------------------------------------------------------------
  // Wachbereitschaft
  // -------------------------------------------------------------------------

  const handleWachbereitschaftDraftChange = (userId, value) => {
    setWachbereitschaftDrafts((prev) => ({ ...prev, [userId]: value }));
  };

  const saveWachbereitschaft = async (userId) => {
    if (!canAssignWachbereitschaft) {
      setError('Keine Berechtigung für diese Aktion');
      return;
    }
    setActionLoading(userId);
    try {
      await client.post(`/api/admin/change-wachbereitschaft/${userId}`, {
        wachbereitschaft: wachbereitschaftDrafts[userId] || null,
      });
      await loadUsers();
      await reloadUser();
      setError('');
    } catch (err) {
      setError(err.response?.data?.error || 'Fehler beim Speichern der Wachbereitschaft');
    } finally {
      setActionLoading(null);
    }
  };

  const handleProfileDraftChange = (userId, field, value) => {
    setProfileDrafts((prev) => ({
      ...prev,
      [userId]: {
        ...(prev[userId] || {}),
        [field]: value,
      },
    }));
  };

  const closeIdentityConfirmDialog = () => {
    setIdentityConfirmDialog({
      open: false,
      userId: null,
      currentFullName: '',
      currentEmail: '',
      nextFullName: '',
      nextEmail: '',
      error: '',
    });
  };

  const requestProfileSave = (userId) => {
    const targetUser = users.find((user) => user.id === userId);
    const draft = profileDrafts[userId];

    if (!targetUser || !draft) {
      return;
    }

    const nextComposedFullName = buildFullName(draft.lastName, draft.firstName);
    const nextEmail = (draft.email || '').trim().toLowerCase();

    if (!nextComposedFullName || !nextEmail) {
      setError('Nachname, Vorname und E-Mail dürfen nicht leer sein');
      return;
    }

    if (!nextEmail.includes('@')) {
      setError('Bitte eine gültige E-Mail-Adresse eingeben');
      return;
    }

    if (nextComposedFullName === (targetUser.full_name || '') && nextEmail === (targetUser.email || '')) {
      setError('Keine Änderungen zum Speichern vorhanden');
      return;
    }

    setIdentityConfirmDialog({
      open: true,
      userId,
      currentFullName: targetUser.full_name || '',
      currentEmail: targetUser.email || '',
      nextFullName: nextComposedFullName,
      nextEmail,
      error: '',
    });
  };

  const confirmProfileSave = async () => {
    if (!identityConfirmDialog.userId) {
      return;
    }

    setActionLoading(identityConfirmDialog.userId);
    try {
      await client.put(`/api/admin/users/${identityConfirmDialog.userId}`, {
        fullName: identityConfirmDialog.nextFullName,
        email: identityConfirmDialog.nextEmail,
      });
      await loadUsers();
      await reloadUser();
      setError('');
      closeIdentityConfirmDialog();
    } catch (err) {
      setIdentityConfirmDialog((prev) => ({
        ...prev,
        error: err.response?.data?.error || 'Fehler beim Speichern der Benutzerdaten',
      }));
    } finally {
      setActionLoading(null);
    }
  };

  // -------------------------------------------------------------------------
  // User status actions
  // -------------------------------------------------------------------------

  const handleActivate = async (userId) => {
    setActionLoading(userId);
    try {
      await client.post(`/api/admin/activate-user/${userId}`);
      await loadUsers();
      setError('');
    } catch (err) {
      setError(err.response?.data?.error || 'Fehler beim Aktivieren');
    } finally {
      setActionLoading(null);
    }
  };

  const handleDeactivate = async (userId) => {
    if (!window.confirm('Möchten Sie diesen Benutzer wirklich deaktivieren?')) return;
    setActionLoading(userId);
    try {
      await client.post(`/api/admin/deactivate-user/${userId}`);
      await loadUsers();
      setError('');
    } catch (err) {
      setError(err.response?.data?.error || 'Fehler beim Deaktivieren');
    } finally {
      setActionLoading(null);
    }
  };

  const handleDelete = async (userId) => {
    if (!window.confirm('Soll dieser Benutzer unwiderruflich gelöscht werden?')) return;
    setActionLoading(userId);
    try {
      await client.delete(`/api/admin/delete-user/${userId}`);
      await loadUsers();
      setError('');
    } catch (err) {
      setError(err.response?.data?.error || 'Fehler beim Löschen');
    } finally {
      setActionLoading(null);
    }
  };

  // -------------------------------------------------------------------------
  // Password reset dialog
  // -------------------------------------------------------------------------

  const openPasswordResetDialog = (targetUser) => {
    if (!canResetPasswords) {
      setError('Nur Admin kann Passwörter zurücksetzen');
      return;
    }
    setPasswordDialog({
      open: true,
      userId: targetUser.id,
      userName: targetUser.full_name,
      password: '',
      confirmPassword: '',
      resetError: '',
      resetSuccess: '',
    });
  };

  const closePasswordResetDialog = () => {
    setPasswordDialog({
      open: false,
      userId: null,
      userName: '',
      password: '',
      confirmPassword: '',
      resetError: '',
      resetSuccess: '',
    });
  };

  // Auto-close dialog shortly after success
  useEffect(() => {
    if (!passwordDialog.open || !passwordDialog.resetSuccess) return;
    const timer = setTimeout(() => closePasswordResetDialog(), 1600);
    return () => clearTimeout(timer);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [passwordDialog.open, passwordDialog.resetSuccess]);

  const submitPasswordReset = async (event) => {
    if (event) event.preventDefault();

    if (!canResetPasswords) {
      setPasswordDialog((prev) => ({ ...prev, resetError: 'Nur Admin kann Passwörter zurücksetzen' }));
      return;
    }
    if (!passwordDialog.password || !passwordDialog.confirmPassword) {
      setPasswordDialog((prev) => ({ ...prev, resetError: 'Bitte beide Passwortfelder ausfüllen' }));
      return;
    }
    if (passwordDialog.password.length < 8) {
      setPasswordDialog((prev) => ({ ...prev, resetError: 'Passwort muss mindestens 8 Zeichen lang sein' }));
      return;
    }
    if (passwordDialog.password !== passwordDialog.confirmPassword) {
      setPasswordDialog((prev) => ({ ...prev, resetError: 'Passwörter stimmen nicht überein' }));
      return;
    }

    setActionLoading(passwordDialog.userId);
    try {
      await client.post(`/api/admin/reset-password/${passwordDialog.userId}`, {
        newPassword: passwordDialog.password,
      });
      await loadUsers();
      setError('');
      setPasswordDialog((prev) => ({ ...prev, resetSuccess: 'Passwort erfolgreich zurückgesetzt', resetError: '' }));
    } catch (err) {
      setPasswordDialog((prev) => ({
        ...prev,
        resetError: err.response?.data?.error || 'Fehler beim Zurücksetzen',
      }));
    } finally {
      setActionLoading(null);
    }
  };

  // -------------------------------------------------------------------------
  // Background upload
  // -------------------------------------------------------------------------

  const handleBackgroundUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setUploading(true);
    setUploadError('');
    const formData = new FormData();
    formData.append('background', file);
    try {
      await client.post('/api/admin/upload-background', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      alert('Hintergrundbild erfolgreich hochgeladen! Bitte Seite neu laden, um das neue Bild zu sehen.');
    } catch {
      setUploadError('Fehler beim Hochladen des Hintergrundbilds');
    } finally {
      setUploading(false);
    }
  };

  // -------------------------------------------------------------------------
  // Provide dialog field update to the dialog component without prop-drilling
  // a separate setter – we attach it to the onClose ref as a convenience.
  // -------------------------------------------------------------------------
  const dialogCloseWithUpdate = closePasswordResetDialog;
  dialogCloseWithUpdate.__updateDialog = setPasswordDialog;

  const openBackupDialog = () => {
    if (!canManageBackups) {
      setError('Keine Berechtigung für Datensicherungen');
      return;
    }

    setBackupError('');
    setBackupSuccess('');
    setSelectedBackupScopeIds(getDefaultSelectedScopes(backupOverview?.scopeGroups || []));
    setRestoreValidation(null);
    setRestoreFile(null);
    setBackupDialogOpen(true);
  };

  const closeBackupDialog = () => {
    if (backupActionLoading) {
      return;
    }

    setBackupDialogOpen(false);
    setRestoreValidation(null);
    setRestoreFile(null);
  };

  const exportBackupFile = async (format) => {
    setBackupActionLoading(format);
    setBackupError('');
    setBackupSuccess('');

    try {
      const response = await client.get(`/api/admin/backups/export/${format}`, {
        params: format === 'json' ? { scopes: selectedBackupScopeIds.join(',') } : undefined,
        responseType: 'blob',
      });
      const fallbackFileName = format === 'csv' ? 'logbuch-export.csv' : 'logbuch-backup.json';
      const fileName = parseDownloadFileName(response.headers['content-disposition'], fallbackFileName);
      triggerBlobDownload(response.data, fileName);
      await loadBackupOverview();
      setBackupSuccess(format === 'csv' ? 'CSV-Export wurde heruntergeladen.' : 'JSON-Backup wurde heruntergeladen.');
    } catch (err) {
      setBackupError(err.response?.data?.error || 'Datei konnte nicht erstellt werden');
    } finally {
      setBackupActionLoading('');
    }
  };

  const toggleBackupScope = (scopeId) => {
    setSelectedBackupScopeIds((previousValue) => (
      previousValue.includes(scopeId)
        ? previousValue.filter((value) => value !== scopeId)
        : [...previousValue, scopeId]
    ));
  };

  const handleBackupRestoreValidation = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = '';

    if (!file) {
      return;
    }

    setBackupActionLoading('validate-restore');
    setBackupError('');
    setBackupSuccess('');
    setRestoreValidation(null);
    setRestoreFile(null);

    try {
      const formData = new FormData();
      formData.append('backupFile', file);
      const response = await client.post('/api/admin/backups/validate', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });

      setRestoreValidation(response.data || null);
      setRestoreFile(file);
      if (response.data?.valid) {
        setBackupSuccess('Sicherungsdatei wurde geprüft. Restore kann jetzt explizit gestartet werden.');
      }
    } catch (err) {
      setBackupError(err.response?.data?.error || 'Sicherungsdatei konnte nicht geprüft werden');
    } finally {
      setBackupActionLoading('');
    }
  };

  const handleBackupRestoreConfirm = async () => {
    if (!restoreFile || !restoreValidation?.valid) {
      setBackupError('Bitte zuerst eine gültige Sicherungsdatei prüfen');
      return;
    }

    if (!window.confirm('Das Einspielen ersetzt alle aktuellen Benutzer-, Rollen- und Logbuchdaten der enthaltenen Bereiche. Fortfahren?')) {
      return;
    }

    setBackupActionLoading('restore');
    setBackupError('');
    setBackupSuccess('');

    try {
      const formData = new FormData();
      formData.append('backupFile', restoreFile);
      const response = await client.post('/api/admin/backups/restore', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });

      await Promise.all([loadBackupOverview(), loadUsers(), reloadUser()]);
      setBackupSuccess(response.data?.message || 'Datensicherung erfolgreich eingespielt');
      setRestoreValidation(null);
      setRestoreFile(null);
    } catch (err) {
      setBackupError(err.response?.data?.error || 'Datensicherung konnte nicht eingespielt werden');
    } finally {
      setBackupActionLoading('');
    }
  };

  const sortedUsers = [...users].sort((leftUser, rightUser) => compareUsers(leftUser, rightUser, sortBy, sortDirection));

  // -------------------------------------------------------------------------
  // Render
  // -------------------------------------------------------------------------

  return (
    <Box sx={{ px: { xs: 1.5, sm: 2, md: 5 }, py: { xs: 2, md: 2.5 } }}>
      <Typography variant="h4" sx={{ mb: 3 }}>
        Benutzerverwaltung
      </Typography>

      {/* Background image upload */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Hintergrundbild ändern
          </Typography>
          <Stack spacing={1.5}>
            <Stack direction="row" spacing={2} alignItems="center">
              <Button variant="outlined" component="label" disabled={uploading} size="small">
                {uploading ? 'Hochladen…' : 'Bild auswählen'}
                <input
                  type="file"
                  accept="image/*"
                  hidden
                  onChange={handleBackgroundUpload}
                />
              </Button>
              {uploading && <CircularProgress size={20} />}
            </Stack>
            {uploadError && <Alert severity="error">{uploadError}</Alert>}
            <Typography variant="caption" color="text.secondary">
              Das neue Bild ersetzt das bisherige Hintergrundbild für alle Nutzer.
              Empfohlen: Querformat, mindestens 1200 px Breite.
            </Typography>
          </Stack>
        </CardContent>
      </Card>

      {/* Global error */}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {/* New-user section */}
      <Box sx={{ mb: 3 }}>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
          {!showNewUserForm ? (
            <Button variant="contained" onClick={() => setShowNewUserForm(true)}>
              + Neuer Benutzer
            </Button>
          ) : (
            <Button variant="outlined" onClick={() => setShowNewUserForm(false)}>
              Benutzeranlage schließen
            </Button>
          )}
          {canManageBackups && (
            <Button variant="outlined" onClick={openBackupDialog}>
              Datensicherung
            </Button>
          )}
        </Stack>
      </Box>

      {showNewUserForm && (
        <NewUserForm
          onCancel={() => setShowNewUserForm(false)}
          onCreated={() => {
            setShowNewUserForm(false);
            loadUsers();
          }}
          setError={setError}
        />
      )}

      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ xs: 'stretch', sm: 'center' }}>
            <FormControl size="small" sx={{ minWidth: 220 }}>
              <InputLabel id="user-sort-by-label">Sortieren nach</InputLabel>
              <Select
                labelId="user-sort-by-label"
                value={sortBy}
                label="Sortieren nach"
                onChange={(event) => setSortBy(event.target.value)}
              >
                {SORT_OPTIONS.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl size="small" sx={{ minWidth: 180 }}>
              <InputLabel id="user-sort-direction-label">Richtung</InputLabel>
              <Select
                labelId="user-sort-direction-label"
                value={sortDirection}
                label="Richtung"
                onChange={(event) => setSortDirection(event.target.value)}
              >
                {SORT_DIRECTIONS.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Stack>
        </CardContent>
      </Card>

      {/* User table */}
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <CircularProgress />
        </Box>
      ) : (
        <TableContainer component={Paper} variant="outlined">
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Vollständiger Name</TableCell>
                <TableCell>E-Mail</TableCell>
                <TableCell>Rollen</TableCell>
                <TableCell>Wachbereitschaft</TableCell>
                <TableCell>Status</TableCell>
                <TableCell />
              </TableRow>
            </TableHead>
            <TableBody>
              {sortedUsers.map((u) => (
                <UserRow
                  key={u.id}
                  user={u}
                  currentUser={currentUser}
                  expanded={expandedUserId === u.id}
                  onToggle={() => setExpandedUserId(expandedUserId === u.id ? null : u.id)}
                  actionLoading={actionLoading}
                  canAssignWachbereitschaft={canAssignWachbereitschaft}
                  canResetPasswords={canResetPasswords}
                  wachbereitschaftDraft={wachbereitschaftDrafts[u.id] ?? ''}
                  onWachbereitschaftDraftChange={handleWachbereitschaftDraftChange}
                  onSaveWachbereitschaft={saveWachbereitschaft}
                  onRoleToggle={handleRoleToggle}
                  onActivate={handleActivate}
                  onDeactivate={handleDeactivate}
                  onDelete={handleDelete}
                  onOpenPasswordReset={openPasswordResetDialog}
                  profileDraft={profileDrafts[u.id]}
                  onProfileDraftChange={handleProfileDraftChange}
                  onRequestProfileSave={requestProfileSave}
                />
              ))}
              {sortedUsers.length === 0 && (
                <TableRow>
                  <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                    <Typography color="text.secondary">Keine Benutzer gefunden</Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Password reset dialog */}
      <PasswordResetDialog
        dialog={passwordDialog}
        onClose={dialogCloseWithUpdate}
        onSubmit={submitPasswordReset}
        actionLoading={actionLoading}
      />
      <UserIdentityConfirmDialog
        dialog={identityConfirmDialog}
        onClose={closeIdentityConfirmDialog}
        onConfirm={confirmProfileSave}
        actionLoading={actionLoading}
      />
      <BackupDialog
        open={backupDialogOpen}
        onClose={closeBackupDialog}
        overview={backupOverview}
        loading={backupLoading}
        actionLoading={backupActionLoading}
        error={backupError}
        success={backupSuccess}
        canRestoreBackups={canRestoreBackups}
        onExportJson={() => exportBackupFile('json')}
        onExportCsv={() => exportBackupFile('csv')}
        selectedScopeIds={selectedBackupScopeIds}
        onScopeToggle={toggleBackupScope}
        restoreValidation={restoreValidation}
        onValidateRestore={handleBackupRestoreValidation}
        onConfirmRestore={handleBackupRestoreConfirm}
      />
    </Box>
  );
}

export default BenutzerPage;
